import $ from 'jquery';
import { v4 as uuid } from 'uuid';
import { createCrossSellPicker } from './cross-sell-picker';

const instances = new WeakMap();

export const initProductCrossSell = (section, { inlinePicker = false, compactActions = false, onPickerToggle = () => {}, onBusy = () => {} } = {}) => {
    if (instances.has(section)) return instances.get(section);
    const cards = new Map();
    let busy = false;
    let nonce = '';

    // Imported media can be missing a resized thumbnail while its original is still available.
    const imageFallback = (image) => {
        const source = image.dataset.crossSellImageFallback;
        if (!source) return;
        delete image.dataset.crossSellImageFallback;
        image.removeAttribute('srcset');
        image.src = source;
    };
    const handleImageError = (event) => {
        if (event.target.matches('img')) imageFallback(event.target);
    };
    section.addEventListener('error', handleImageError, true);
    section.querySelectorAll('img').forEach((image) => {
        if (image.complete && !image.naturalWidth) imageFallback(image);
    });

    const render = (card, state) => {
        const add = card.querySelector('[data-cross-sell-add]');
        const undo = card.querySelector('[data-cross-sell-undo]');
        add.classList.toggle('is-added', !!state.key);
        const choose = card.dataset.requiresSelection === 'true';
        const label = state.key ? 'Dodano do koszyka' : (state.retry ? 'Spróbuj ponownie' : (choose ? 'Wybierz wariant' : 'Dodaj do koszyka'));
        add.querySelector('[data-cross-sell-label]').textContent = compactActions && !state.retry
            ? (state.key ? 'W koszyku' : (choose ? 'Wybierz wariant' : 'Dodaj')) : label;
        const icon = add.querySelector('[data-cross-sell-icon]');
        icon.hidden = !state.key && (choose || state.retry);
        icon.textContent = state.key ? '✓' : '+';
        add.setAttribute('aria-label', `${label}: ${card.querySelector('.ProductCrossSell__name').textContent.trim()}, ${card.querySelector('.ProductCrossSell__variant').textContent.trim()}`);
        add.disabled = busy || !!state.key;
        undo.hidden = !state.key;
        undo.disabled = busy;
    };
    const announce = (card, message, error = false) => {
        const notice = card.querySelector('[data-cross-sell-notice]');
        notice.textContent = message;
        notice.classList.toggle('is-error', error);
        notice.hidden = !message;
    };
    section.querySelectorAll('[data-cross-sell-card]').forEach((card) => {
        cards.set(card, { key: '', request: uuid(), selection: null, pendingSelection: null, retry: false });
    });
    section.classList.add('is-ready');

    const getNonce = async () => {
        if (nonce) return nonce;
        const data = await $.ajax({
            url: jsData.ajaxUrl, type: 'GET', dataType: 'json', cache: false, timeout: 20000,
            data: { action: 'verenza_get_cart_items' },
        });
        if (!data.nonce) throw new Error('Nie udało się odczytać koszyka. Spróbuj ponownie.');
        nonce = data.nonce;
        return nonce;
    };

    const mutate = async (card, undo = false, selected = null) => {
        const state = cards.get(card);
        const button = card.querySelector('[data-cross-sell-add]');
        const selection = undo ? null : (selected || state.pendingSelection || {
            id: Number(card.dataset.variationId), attributes: JSON.parse(card.dataset.attributes),
        });
        if (!undo) state.pendingSelection = selection;
        let result = { success: false, message: '' };
        busy = true;
        onBusy(true);
        cards.forEach((value, element) => render(element, value));
        card.setAttribute('aria-busy', 'true');
        announce(card, '');
        if (!undo) button.querySelector('[data-cross-sell-label]').textContent = 'Dodajemy…';
        try {
            const response = await $.ajax({
                url: jsData.ajaxUrl, type: 'POST', dataType: 'json', timeout: 20000,
                data: {
                    action: 'ravessa_sidebar_cart_action',
                    operation: undo ? 'cross_sell_undo' : 'cross_sell_add',
                    nonce: await getNonce(), request_id: state.request,
                    product_id: card.dataset.productId, variation_id: selection?.id || 0,
                    attributes: selection?.attributes || {}, quantity: 1,
                },
            });
            if (!response.success || !response.data || typeof response.data.cart_html !== 'string') {
                throw new Error(response.data?.message || 'Nie udało się potwierdzić zmiany. Spróbuj ponownie.');
            }
            state.key = undo ? '' : response.data.cross_sell_key;
            if (undo) state.request = uuid();
            if (!undo && selection.label !== undefined) {
                state.selection = selection;
                card.dataset.variationId = String(selection.id);
                card.dataset.attributes = JSON.stringify(selection.attributes);
                card.querySelector('.ProductCrossSell__variant').textContent = selection.label;
                card.querySelector('.ProductCrossSell__price').innerHTML = selection.price;
                card.dataset.crossSellAmount = String(selection.amount);
                card.dataset.crossSellPrice = selection.amount_html;
                const cheapest = [...cards.keys()].sort((a, b) => Number(a.dataset.crossSellAmount) - Number(b.dataset.crossSellAmount))[0];
                const fromPrice = section.querySelector('[data-cross-sell-from-price]');
                if (fromPrice) fromPrice.innerHTML = cheapest.dataset.crossSellPrice;
                card.querySelector('.ProductCrossSell__image').innerHTML = selection.image;
                card.querySelectorAll('a').forEach((link) => {
                    const url = new URL(selection.url);
                    Object.entries(selection.attributes).forEach(([name, value]) => url.searchParams.set(name, value));
                    link.href = url.href;
                });
                const image = card.querySelector('.ProductCrossSell__image img');
                if (image) image.alt = `${card.querySelector('.ProductCrossSell__name').textContent.trim()} — ${selection.label}`;
            }
            state.retry = false;
            state.pendingSelection = null;
            result.success = true;
            nonce = response.data.nonce || nonce;
            $(document.body).trigger('ravessa:cart-updated', [response.data]);
            $(document.body).trigger('wc_fragment_refresh');
            // Confirmation is announced without shifting focus or opening the cart drawer.
            announce(card, undo ? 'Cofnięto dodanie produktu.' : 'Produkt został dodany do koszyka.');
        } catch (error) {
            const data = error.responseJSON?.data;
            if (data?.refresh) nonce = '';
            result.message = data?.message || error.message || 'Nie udało się potwierdzić zmiany. Sprawdź połączenie i spróbuj ponownie.';
            if (!undo) {
                state.retry = !data;
                if (data) { state.pendingSelection = null; state.request = uuid(); }
            }
            announce(card, result.message, true);
        } finally {
            busy = false;
            onBusy(false);
            card.setAttribute('aria-busy', 'false');
            cards.forEach((value, element) => render(element, value));
            if (!section.querySelector('[data-cross-sell-picker]').open && undo && !state.key) card.querySelector('[data-cross-sell-add]').focus({ preventScroll: true });
            else if (!section.querySelector('[data-cross-sell-picker]').open && state.key) card.querySelector('[data-cross-sell-undo]').focus({ preventScroll: true });
        }
        return result;
    };
    const picker = createCrossSellPicker(section, {
        add: (card, selection) => mutate(card, false, selection),
        rememberNonce: (value) => { nonce = value || nonce; },
        isBusy: () => busy,
        inline: inlinePicker,
        onToggle: onPickerToggle,
    });
    const handleClick = (event) => {
        const button = event.target.closest('[data-cross-sell-add], [data-cross-sell-undo]');
        if (!button || busy) return;
        const card = button.closest('[data-cross-sell-card]');
        const state = cards.get(card);
        const undo = button.hasAttribute('data-cross-sell-undo');
        if (!undo && state.key) return;
        if (!undo && !state.retry && card.dataset.requiresSelection === 'true') picker.open(card, state);
        else mutate(card, undo);
    };
    section.addEventListener('click', handleClick);

    const handleCartRendered = (event) => {
        cards.forEach((state, card) => {
            if (state.key && !event.detail.keys.includes(state.key)) {
                state.key = '';
                state.request = uuid();
                announce(card, '');
                render(card, state);
            }
        });
    };
    document.addEventListener('ravessa:cart-rendered', handleCartRendered);
    const controller = {
        isBusy: () => busy,
        isPickerOpen: picker.isOpen,
        closePicker: picker.close,
        destroy: () => {
            picker.destroy();
            section.removeEventListener('error', handleImageError, true);
            section.removeEventListener('click', handleClick);
            document.removeEventListener('ravessa:cart-rendered', handleCartRendered);
            instances.delete(section);
        },
    };
    instances.set(section, controller);
    return controller;
};

document.querySelectorAll('.ProductCrossSell').forEach((section) => initProductCrossSell(section));
